import React, { useState, useEffect } from "react";
import axios from "axios";
import Select from "react-select";
import { toast } from "react-toastify";
import { useParams, useNavigate } from "react-router-dom";
import useTranslations from "../../hooks/useTranslations";
import useUserLanguage from "../../hooks/useUserLanguage";
/** Tree -> seçenek listesi */
const flattenTreeForSelect = (nodes, level = 0) =>
  nodes.flatMap((n) => {
    const indent = "\u00A0".repeat(level * 3);
    const opt = {
      value: n.value,
      label: `${indent}${level ? "↳ " : ""}${n.label}`,
    };
    const kids = Array.isArray(n.children)
      ? flattenTreeForSelect(n.children, level + 1)
      : [];
    return [opt, ...kids];
  });

const FeatureEdit = () => {
  const { id } = useParams(); // feature id
  const navigate = useNavigate();

  const language = useUserLanguage();
  const { translations } = useTranslations(language);
  const getText = (key, def) => translations[key] || def;

  const [categories, setCategories] = useState([]);
  const [form, setForm] = useState({
    title: "",
    category_ids: [],
    is_required: false,
    is_multiple: false,
    input_type: "none",
    values: [],
  });

  const [newValue, setNewValue] = useState("");
  const [displayMode, setDisplayMode] = useState("none-single");
  const [editModal, setEditModal] = useState({
    open: false,
    index: null,
    value: "",
    status: "aktif",
  });

  useEffect(() => {
    if (form.input_type === "none" && !form.is_multiple)
      setDisplayMode("none-single");
    if (form.input_type === "none" && form.is_multiple)
      setDisplayMode("none-multi");
    if (form.input_type === "select" && !form.is_multiple)
      setDisplayMode("select-single");
    if (form.input_type === "select" && form.is_multiple)
      setDisplayMode("select-multi");
  }, [form]);

  useEffect(() => {
    if (displayMode === "none-single")
      setForm((f) => ({ ...f, is_multiple: false, input_type: "none" }));

    if (displayMode === "none-multi")
      setForm((f) => ({ ...f, is_multiple: true, input_type: "none" }));

    if (displayMode === "select-single")
      setForm((f) => ({ ...f, is_multiple: false, input_type: "select" }));

    if (displayMode === "select-multi")
      setForm((f) => ({ ...f, is_multiple: true, input_type: "select" }));
  }, [displayMode]);

  useEffect(() => {
    // kategori listesi çek
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/stock-categories/tree`)
      .then((res) => {
        const flat = flattenTreeForSelect(res.data);
        setCategories(flat);
      });

    // feature fetch
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/api/features/${id}`)
      .then((res) => {
        const data = res.data;

        setForm({
          title: data.title ?? "",
          category_ids: Array.isArray(data.category_ids)
            ? data.category_ids.map((cid) => ({
                value: cid,
                label: data.category_names ? data.category_names[cid] : "",
              }))
            : [],
          is_required: data.is_required ?? false,
          is_multiple: data.is_multiple ?? false,
          input_type: data.input_type ?? "none",
          values: Array.isArray(data.values)
            ? data.values.map(
                (v) =>
                  typeof v === "string"
                    ? { id: null, value: v, status: "aktif" }
                    : v, // zaten objeyse dokunma
              )
            : [],
        });
      });
  }, [id]);

  const updateValue = (index, val) => {
    let updated = [...form.values];
    updated[index] = val;
    setForm({ ...form, values: updated });
  };

  const removeValue = (index) => {
    let updated = [...form.values];
    updated.splice(index, 1);
    setForm({ ...form, values: updated });
  };

  const addValue = () => {
    if (!newValue.trim()) return;
    setForm({
      ...form,
      values: [
        ...form.values,
        { id: null, value: newValue.trim(), status: "aktif" },
      ],
    });

    setNewValue("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      ...form,
      category_ids: form.category_ids.map((x) => x.value),
    };

    await axios.put(
      `${process.env.REACT_APP_API_BASE_URL}/api/features/${id}`,
      payload,
    );

    toast.success(getText("FEATURE_UPDATED_SUCCESS"));

    navigate("/features/table");
  };

  return (
    <div className="container mt-4 card p-4">

      <label className="form-label fw-bold">{getText("INCLUDED_CATEGORIES_LABEL")}</label>
      <Select
        isMulti
        options={categories}
        value={form.category_ids}
        onChange={(v) => setForm({ ...form, category_ids: v })}
      />
      <label className="form-label fw-bold mt-3">{getText("DATA_INPUT_LABEL")}</label>
      <div className="form-check">
        <input
          type="checkbox"
          className="form-check-input"
          checked={form.is_required}
          onChange={(e) => setForm({ ...form, is_required: e.target.checked })}
        />
        <label className="form-check-label">  {getText("REQUIRED_LABEL")}</label>
      </div>

      <label className="form-label fw-bold mt-3"> {getText("DISPLAY_TYPE_LABEL")}</label>

      <div className="form-check">
        <input
          type="radio"
          className="form-check-input"
          name="ftype"
          value="none-single"
          checked={displayMode === "none-single"}
          onChange={(e) => setDisplayMode(e.target.value)}
        />
        <label className="form-check-label">{getText("SINGLE_BOX_OPTION")}</label>
      </div>

      <div className="form-check">
        <input
          type="radio"
          className="form-check-input"
          name="ftype"
          value="none-multi"
          checked={displayMode === "none-multi"}
          onChange={(e) => setDisplayMode(e.target.value)}
        />
        <label className="form-check-label">{getText("MULTI_BOX_OPTION")}</label>
      </div>

      <div className="form-check">
        <input
          type="radio"
          className="form-check-input"
          name="ftype"
          value="select-single"
          checked={displayMode === "select-single"}
          onChange={(e) => setDisplayMode(e.target.value)}
        />
        <label className="form-check-label">{getText("SINGLE_SELECT_OPTION")}</label>
      </div>

      <div className="form-check mb-3">
        <input
          type="radio"
          className="form-check-input"
          name="ftype"
          value="select-multi"
          checked={displayMode === "select-multi"}
          onChange={(e) => setDisplayMode(e.target.value)}
        />
        <label className="form-check-label">{getText("MULTI_SELECT_OPTION")}</label>
      </div>

      <label className="form-label fw-bold">{getText("FEATURE_TITLE_LABEL")}</label>
      <input
        className="form-control mb-3"
        value={form.title}
        onChange={(e) => setForm({ ...form, title: e.target.value })}
      />

      <label className="form-label fw-bold">{getText("VALUES_LABEL")}</label>

      {form.values.map((v, i) => (
        <div
          key={i}
          className="d-flex mb-2 align-items-center gap-2 border p-2 rounded"
        >
          <span className="flex-grow-1">{v.value}</span>

          {/* Status switch */}
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              checked={v.status === "aktif"}
              onChange={() => {
                const updated = [...form.values];
                updated[i].status = v.status === "aktif" ? "pasif" : "aktif";
                setForm({ ...form, values: updated });
              }}
            />
          </div>

          {/* Düzenle */}
          <button
            className="btn btn-sm btn-warning"
            onClick={() =>
              setEditModal({
                open: true,
                index: i,
                value: v.value,
                status: v.status,
              })
            }
          >
            {getText("EDIT")}
          </button>

          {/* Sil */}
          <button
            className="btn btn-sm btn-danger"
            onClick={() => removeValue(i)}
          >
            {getText("DELETE")}
          </button>
        </div>
      ))}
      {/* edit modal */}
      {editModal.open && (
        <div
          className="modal d-block"
          style={{ background: "rgba(0,0,0,0.4)" }}
        >
          <div className="modal-dialog">
            <div className="modal-content p-3">

              <input
                className="form-control mt-2"
                value={editModal.value}
                onChange={(e) =>
                  setEditModal({ ...editModal, value: e.target.value })
                }
              />

              <div className="form-check form-switch mt-2">
                <label className="form-check-label me-2">{getText("STATUS_ACTIVE")}</label>
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={editModal.status === "aktif"}
                  onChange={() =>
                    setEditModal({
                      ...editModal,
                      status: editModal.status === "aktif" ? "pasif" : "aktif",
                    })
                  }
                />
              </div>

              <div className="d-flex justify-content-end gap-2 mt-3">
                <button
                  className="btn btn-secondary"
                  onClick={() =>
                    setEditModal({
                      open: false,
                      index: null,
                      value: "",
                      status: "aktif",
                    })
                  }
                >
                  {getText("CLOSE_BUTTON")}
                </button>

                <button
                  className="btn btn-primary"
                  onClick={() => {
                    const updated = [...form.values];
                    updated[editModal.index] = {
                      value: editModal.value,
                      status: editModal.status,
                    };
                    setForm({ ...form, values: updated });
                    setEditModal({
                      open: false,
                      index: null,
                      value: "",
                      status: "aktif",
                    });
                  }}
                >
                  {getText("SAVE")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="d-flex gap-2 mb-3">
        <input
          className="form-control"
         placeholder={getText("ADD_NEW_VALUE_PLACEHOLDER")}
          value={newValue}
          onChange={(e) => setNewValue(e.target.value)}
        />
        <button className="btn btn-secondary" onClick={addValue}>
            {getText("ADD_BUTTON")}
        </button>
      </div>
      <div className="d-flex justify-content-center">
        <button onClick={handleSubmit} className="btn btn-primary">
          {getText("SAVE")}
        </button>
      </div>
    </div>
  );
};

export default FeatureEdit;
